import { useEffect, useState } from "react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

/**
 * Renders an <img> OR <video> depending on what the admin uploaded for the slug.
 *
 *  - On mount, does a single HEAD request against /catalog-images/<slug>.
 *  - 200 + Content-Type "video/*"  → <video autoplay muted loop playsinline>
 *  - 200 + Content-Type "image/*"  → <img>
 *  - 404 / network fail            → <img src={fallback}> (bundled default)
 *
 * Same API surface as the legacy <BrandImage>, so every consumer
 * (Home, About, ForClinics, RND, Login) automatically supports MP4 / WebM
 * the moment the admin uploads one — no per-page changes needed.
 *
 * `className` is applied to whichever tag renders. `object-cover` works on
 * both <img> and <video>, so the existing height-locked slot styles
 * (`h-[460px] object-cover` etc.) keep working for videos too — they fill
 * the box exactly like images do.
 */
export function BrandImage({ slug, fallback, alt = "", className = "", ...rest }) {
    const [state, setState] = useState({ kind: "loading", url: null });

    useEffect(() => {
        let cancelled = false;
        const url = `${API}/catalog-images/${slug}`;
        fetch(url, { method: "HEAD" })
            .then((r) => {
                if (cancelled) return;
                if (!r.ok) { setState({ kind: "img", url: fallback }); return; }
                const ct = (r.headers.get("Content-Type") || "").toLowerCase();
                const v = r.headers.get("X-Updated-At") || Date.now();
                const cacheBusted = `${url}?v=${encodeURIComponent(v)}`;
                if (ct.startsWith("video/")) {
                    setState({ kind: "video", url: cacheBusted });
                } else {
                    setState({ kind: "img", url: cacheBusted });
                }
            })
            .catch(() => { if (!cancelled) setState({ kind: "img", url: fallback }); });
        return () => { cancelled = true; };
    }, [slug, fallback]);

    if (state.kind === "video") {
        return (
            <video
                src={state.url}
                className={className}
                autoPlay
                muted
                loop
                playsInline
                preload="metadata"
                aria-label={alt}
                {...rest}
            />
        );
    }

    // Loading state shows the fallback so there's no layout shift / flash.
    const src = state.url || fallback;
    return (
        <img
            src={src}
            alt={alt}
            className={className}
            onError={(e) => {
                if (fallback && !e.currentTarget.src.endsWith(fallback)) {
                    e.currentTarget.src = fallback;
                }
            }}
            {...rest}
        />
    );
}

/**
 * Dynamically swap <link rel="icon"> to the uploaded favicon if one exists.
 * Falls back silently to whatever is hard-coded in public/index.html.
 */
export function useDynamicFavicon() {
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/catalog-images/favicon`, { method: "HEAD" })
            .then((r) => {
                if (cancelled || !r.ok) return;
                const url = `${API}/catalog-images/favicon?v=${r.headers.get("x-updated-at") || Date.now()}`;
                document.querySelectorAll('link[rel="icon"], link[rel="apple-touch-icon"]').forEach((el) => {
                    el.setAttribute("href", url);
                });
            })
            .catch(() => {});
        return () => { cancelled = true; };
    }, []);
}

/**
 * Probe a slug — returns true if an admin override exists for it.
 * Useful for components that want to know whether to apply a special style.
 */
export function useHasOverride(slug) {
    const [has, setHas] = useState(false);
    useEffect(() => {
        let cancelled = false;
        fetch(`${API}/catalog-images/${slug}`, { method: "HEAD" })
            .then((r) => { if (!cancelled) setHas(r.ok); })
            .catch(() => { if (!cancelled) setHas(false); });
        return () => { cancelled = true; };
    }, [slug]);
    return has;
}
